/**
 * The sound switch a visitor can reach before they have an account.
 *
 * `main.ts` arms the menu bed on the first gesture on any auth screen (`ensureAuthMusic`), and
 * until now the only control over it was Settings → Game → Menu music — which lives behind the
 * login form. So music started at a stranger and could not be stopped without signing up
 * (2026-09-10). This is that control, and it
 * is only a control: nothing here starts a sound, and the landing page stays silent.
 *
 * It owns no state. `musicEnabled` in `engine/settings.ts` is already the hard off switch that
 * `menu-music.ts` obeys, and `saveSettings` already emits `bl:settings-changed`, which that
 * player listens to — so flipping the flag silences a bed that is mid-loop, live, with no
 * reload. The toggle repaints from the same event, which is what keeps it honest when the
 * Settings screen (or a second toggle: the bar has one, so does the drawer) moves the flag.
 *
 * Built as a plate in the same shape as `i18n/lang-pick.ts`: a painted `plate-key` with the
 * 3D speaker icon, ≥44x44 so a thumb can hit it, `aria-pressed` rather than a label that
 * lies about what a press will do, and motion `motion-safe:` only. The bar form is icon-only
 * because the bar already carries two CTAs and a language picker at 320px; the drawer form
 * (`compact`) has a whole row and spells the state out.
 */
import { loadSettings, saveSettings, type PlayerSettings } from '../settings';
import { t } from '../i18n';
import { icon, plateChrome, plateContent } from './ui';

export interface SoundToggleOptions {
  /** Drawer form: full-width row with the state written out beside the icon. */
  compact?: boolean;
  /** Top-bar form: the square key, icon only — same painted key, no spelled-out state. */
  bare?: boolean;
}

/**
 * The key is a painted steel plate (`plate-key`, kit.css) wearing the 3D speaker icon — the
 * bar form is a square 44px key (`plate-icon`), the drawer form a full-width row. Off is not a
 * colour change alone: the speaker greys out and a red cross lands on it, so the state survives
 * for anyone who cannot see the colour.
 */
function speaker(muted: boolean): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'relative inline-flex';
  wrap.append(icon('sound', { size: 28, className: muted ? '[filter:grayscale(1)_brightness(0.7)]' : '' }));
  if (muted) wrap.append(icon('close', { size: 20, className: 'absolute -right-1 -bottom-1' }));
  return wrap;
}

export function mountSoundToggle(host: HTMLElement, opts: SoundToggleOptions = {}): () => void {
  const compact = opts.compact === true;

  const button = document.createElement('button');
  button.type = 'button';
  // The bar form (`bare` or default) is the square key; only the drawer row is wide.
  plateChrome(button, '', {
    variant: 'secondary',
    size: 'md',
    className: compact ? 'w-full gap-3' : 'plate-icon',
  });
  // `data-sound-toggle`, NOT `data-slot`: the host in the bar already carries
  // `data-slot="sound-toggle"`, so reusing the name here made one selector match the seat, this
  // button and the drawer's copy of it — three nodes for one query. A widget names its own
  // parts; the slot name belongs to whoever reserved the seat.
  button.dataset.soundToggle = 'button';

  const label = document.createElement('span');
  label.dataset.slot = 'sound-toggle-label';
  // Hidden from sight in the bar form, never from a screen reader: `aria-pressed` says whether
  // it is on, and this says what "it" is.
  label.className = compact ? '' : 'sr-only';

  host.replaceChildren(button);

  const paint = (settings: PlayerSettings = loadSettings()): void => {
    const on = settings.musicEnabled;
    button.setAttribute('aria-pressed', String(on));
    button.setAttribute('aria-label', t('common.toggleSound'));
    const text = t(on ? 'common.soundOn' : 'common.soundOff');
    button.title = text;
    label.textContent = text;
    // Into the plate's own label node: the face paints over any other child.
    plateContent(button).replaceChildren(speaker(!on), label);
  };

  const onClick = (): void => {
    // Read through `loadSettings` rather than trusting the painted state: the Settings screen
    // and the other toggle write the same flag, and the last write must be what we invert.
    const next = !loadSettings().musicEnabled;
    paint(saveSettings({ musicEnabled: next }));
  };

  // `saveSettings` fires this itself, so our own click repaints twice — cheap, and it is the
  // reason a change made anywhere else lands here too.
  const onSettingsChanged = (event: Event): void => {
    paint((event as CustomEvent<PlayerSettings>).detail ?? loadSettings());
  };

  button.addEventListener('click', onClick);
  window.addEventListener('bl:settings-changed', onSettingsChanged);

  paint();

  // No locale subscription: a language change remounts the whole page (`main.ts`
  // `remountForLocale`), so this widget is rebuilt rather than repainted.
  return () => {
    button.removeEventListener('click', onClick);
    window.removeEventListener('bl:settings-changed', onSettingsChanged);
    button.remove();
  };
}
